import { useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useAuthContext } from '@/hooks/useAuthContext';
import { storage, db } from '@/lib/firebase';
import { ref, uploadBytes } from 'firebase/storage';
import { doc, setDoc, serverTimestamp, collection } from 'firebase/firestore';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Loader2, FileText } from 'lucide-react';
import { toast } from 'sonner';
import ExtractionSidebar, {
  type ExtractionStep,
} from '@/components/documents/ExtractionSidebar';
import { documentsApi } from '@/redux/api/documents.rtkq';
import { accountsApi, useGetAccountByIdQuery } from '@/redux/api/accounts.rtkq';
import { QuotaDisplay } from '@/components/documents/QuotaDisplay';
import InsufficientCreditsDialog from '@/components/InsufficientCreditsDialog';
import { PDFDocument } from 'pdf-lib';
import {
  DOCUMENT_MAX_UPLOAD_BYTES,
  useExtractPdfMutation,
  useAssertCanChargeMutation,
  type OcrProgress,
} from '@/redux/api/ocr.rtkq';
import {
  trackOcrExtractionStarted,
  trackOcrExtractionCompleted,
  trackOcrExtractionFailed,
  trackWallShown,
  trackOcrExtractionCancelled,
} from '@/lib/mixpanel';

// Helper function to count PDF pages
const countPdfPages = async (file: File): Promise<number> => {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await PDFDocument.load(arrayBuffer);
    return pdf.getPageCount();
  } catch (error) {
    console.error('countPdfPages: Error counting PDF pages:', error);
    return 1; // Default to 1 if counting fails
  }
};

const SCAN_APP_ID = 'scan-documents-extraction';

const isInsufficientCreditsError = (error: unknown): boolean => {
  if (error instanceof Error && error.message === 'INSUFFICIENT_CREDITS') {
    return true;
  }
  const code = (error as { code?: string })?.code;
  if (code === 'insufficient_credits') {
    return true;
  }
  const status = (error as { status?: number })?.status;
  const data = (error as { data?: { code?: string } })?.data;
  if (
    status === 402 ||
    data?.code === 'INSUFFICIENT_CREDITS' ||
    data?.code === 'USAGE_WINDOW_EXHAUSTED'
  ) {
    return true;
  }
  return (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    (error as { code?: string }).code === 'functions/resource-exhausted'
  );
};

function PdfExtractor() {
  const dispatch = useDispatch();
  const { user, getAccountId } = useAuthContext();
  const officeId = getAccountId();
  const { data: officeAccount } = useGetAccountByIdQuery(
    { accountId: officeId! },
    { skip: !officeId },
  );
  const [extractPdf] = useExtractPdfMutation();
  const [assertCanCharge] = useAssertCanChargeMutation();
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isScanned, setIsScanned] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  // Sidebar state
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState<ExtractionStep>('uploading');
  const [progress, setProgress] = useState<OcrProgress | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [insufficientCreditsOpen, setInsufficientCreditsOpen] = useState(false);
  const [showFileTooLargeDialog, setShowFileTooLargeDialog] = useState(false);
  const [creditWall, setCreditWall] = useState<{
    wall: string;
    quotaWindowEndsAt?: string | null;
  } | null>(null);

  // Ref for file input to reset it
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Cancels the in-flight extraction. The backend flow is plain fetch all the
  // way down, so a real AbortController replaces the polled boolean the old
  // AI-SDK stream needed.
  const abortRef = useRef<AbortController | null>(null);

  // Helper function to reset file and file input
  const resetFile = () => {
    setFile(null);
    setProgress(null);
    // Reset the file input element to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Shared validation function for file input and drag & drop
  const validateAndSetFile = (selectedFile: File) => {
    if (selectedFile.size > DOCUMENT_MAX_UPLOAD_BYTES) {
      setShowFileTooLargeDialog(true);
      return;
    }

    // Validate file type
    if (selectedFile.type !== 'application/pdf') {
      toast.error('נא להעלות קובץ PDF בלבד');
      return;
    }

    setFile(selectedFile);
    setProgress(null);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      validateAndSetFile(e.target.files[0]);
    }
  };

  // Drag and drop event handlers
  const handleDragEnter = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragOver = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();

    // Only set isDragging to false if we're actually leaving the drop zone
    // Check if the related target is not a child of the current target
    if (e.currentTarget.contains(e.relatedTarget as Node)) {
      return;
    }
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;

    if (files.length === 0) {
      return;
    }

    // Handle multiple files - only accept the first one
    if (files.length > 1) {
      toast.error('ניתן לבחור קובץ אחד בלבד');
      validateAndSetFile(files[0]);
      return;
    }

    validateAndSetFile(files[0]);
  };

  const handleExtract = async () => {
    if (!file || !user) return;

    // Open sidebar and reset state
    setSidebarOpen(true);
    setProgress(null);
    setErrorMessage('');
    setCurrentStep('uploading');

    const controller = new AbortController();
    abortRef.current = controller;
    const throwIfCancelled = () => {
      if (controller.signal.aborted) {
        throw new Error('EXTRACTION_CANCELLED');
      }
    };

    const startTime = Date.now();
    // Hoisted so the failure/cancellation events below can still report it.
    let pageCount: number | undefined;

    try {
      setIsUploading(true);

      // 0. Count PDF pages — Nest bills 1 credit per page after OCR
      pageCount = await countPdfPages(file);
      throwIfCancelled();

      const creditCost =
        officeAccount?.subscription?.plan?.includedApps?.find(
          (app) => app.appIdentifier === SCAN_APP_ID,
        )?.creditCost ?? 1;
      const creditsNeeded = (pageCount ?? 1) * creditCost;

      // 0.5 Ask Nest whether the office can afford this OCR run. A local
      // balance check would show the paywall without hitting billing, so
      // Slack would never fire. Server-side assertCanCharge 402s (and
      // pings Slack) without deducting or uploading.
      try {
        await assertCanCharge({
          appId: SCAN_APP_ID,
          units: pageCount ?? 1,
        }).unwrap();
      } catch (error) {
        if (isInsufficientCreditsError(error)) {
          const data = (error as { data?: { code?: string } })?.data;
          const wallError = new Error(data?.code || 'INSUFFICIENT_CREDITS') as Error & {
            billing?: { code?: string; required?: number; available?: number; quotaWindowEndsAt?: string | null };
          };
          wallError.billing = data;
          throw wallError;
        }
        throw error;
      }
      throwIfCancelled();

      // The backend resolves every upload against an office; without one the
      // Nest guard rejects the request with a bare 403.
      if (!officeId) {
        throw new Error('NO_OFFICE');
      }

      trackOcrExtractionStarted({
        officeId,
        pageCount,
        fileSizeBytes: file.size,
        isScanned,
      });

      // 1. Keep the original in Firebase Storage — originalPath still backs the
      // viewer's "download original" and the delete mutation.
      const storagePath = `${user.uid}/documents/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, file);
      throwIfCancelled();

      setIsUploading(false);
      setIsProcessing(true);

      // 2. Run the extraction on the backend OCR pipeline.
      const result = await extractPdf({
        file,
        officeId,
        signal: controller.signal,
        onProgress: (update: OcrProgress) => {
          setProgress(update);
          setCurrentStep(
            update.step === 'uploading' || update.step === 'preparing'
              ? 'uploading'
              : update.step === 'queued'
                ? 'queued'
                : 'extracting',
          );
        },
      }).unwrap();
      throwIfCancelled();
      if (result.renamedTo) {
        toast.info(`קובץ בשם זה כבר קיים בתיק. נשמר בשם ${result.renamedTo}`);
      }

      setCurrentStep('saving');

      // 3. Save to Firestore with enhanced metadata
      const documentsCollection = collection(
        db,
        'users',
        user.uid,
        'documents',
      );
      const docRef = doc(documentsCollection);
      const docId = docRef.id;

      await setDoc(docRef, {
        docId: docId,
        originalPath: storagePath,
        storageFileName: file.name,
        extractedContent: result.markdown,
        contentFormat: 'markdown',
        type: 'pdf_extraction',
        isScanned: isScanned,
        processingStatus: 'completed',
        processingModel: 'globalbit-ocr',
        pageCount: result.pagesTotal || pageCount,
        creditsConsumed: result.reused ? 0 : creditsNeeded,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        accountId: officeId,
        caseId: result.caseId,
        fileId: result.fileId,
        fileSize: file.size,
        contentType: file.type,
        extractionDuration: Date.now() - startTime,
      });

      // Nest OCR worker charges scan-documents-extraction per DONE page.
      // Skipped when the backend served a previous extraction of the same file.

      // 5. Invalidate RTK Query cache to refresh the documents table and usage stats
      dispatch(documentsApi.util.invalidateTags(['Documents']));
      dispatch(
        accountsApi.util.invalidateTags([{ type: 'Accounts', id: officeId }]),
      );

      trackOcrExtractionCompleted({
        officeId,
        pageCount,
        pagesTotal: result.pagesTotal,
        pagesFailed: result.pagesFailed,
        durationMs: Date.now() - startTime,
        reused: Boolean(result.reused),
        creditsConsumed: result.reused ? 0 : creditsNeeded,
      });

      setCurrentStep('completed');

      // Auto-close sidebar after 1 second
      setTimeout(() => {
        setSidebarOpen(false);
        resetFile(); // Reset file selection and input
        toast.success(
          result.reused
            ? 'המסמך כבר עובד במשרד — נטען החילוץ הקיים, ללא חיוב קרדיטים'
            : 'המסמך חולץ בהצלחה!',
        );
      }, 1000);
    } catch (error) {
      const code = (error as { code?: string })?.code;
      const message = error instanceof Error ? error.message : '';

      if (code === 'cancelled' || message === 'EXTRACTION_CANCELLED') {
        trackOcrExtractionCancelled({
          officeId: getAccountId(),
          pageCount,
          durationMs: Date.now() - startTime,
        });
        setSidebarOpen(false);
        resetFile();
        toast.info('החילוץ בוטל');
        return;
      }

      const fail = (text: string) => {
        trackOcrExtractionFailed({
          officeId: getAccountId(),
          pageCount,
          durationMs: Date.now() - startTime,
          errorCode: code || message || 'unknown',
        });
        setErrorMessage(text);
        setCurrentStep('error');
        toast.error(text);
      };

      if (isInsufficientCreditsError(error)) {
        const billing = (error as {
          billing?: {
            code?: string;
            required?: number;
            available?: number;
            quotaWindowEndsAt?: string | null;
          };
        }).billing;
        const wallCode = billing?.code || 'INSUFFICIENT_CREDITS';
        const wall =
          wallCode === 'USAGE_WINDOW_EXHAUSTED' ? 'window_exhausted' : 'insufficient_credits';
        trackWallShown({
          wall,
          app: 'scan-documents-extraction',
          required: billing?.required,
          available: billing?.available,
          window_ends_at: billing?.quotaWindowEndsAt,
          surface: 'web',
          feature: 'ocr_preflight',
          ui: 'dialog',
          message_key: wallCode,
        });
        setCreditWall({ wall, quotaWindowEndsAt: billing?.quotaWindowEndsAt });
        setInsufficientCreditsOpen(true);
        fail('אין מספיק קרדיטים לחילוץ המסמך');
        return;
      }
      if (message === 'NO_OFFICE' || code === 'no_office') {
        fail('לא נמצא משרד משויך למשתמש. יש להתחבר מחדש ולנסות שוב.');
        return;
      }
      if (code === 'file_too_large') {
        setSidebarOpen(false);
        setShowFileTooLargeDialog(true);
        return;
      }
      if (code === 'upload_blocked') {
        // A bucket CORS rejection — invisible in the console beyond a bare
        // TypeError, so name it rather than blaming the network.
        fail('ההעלאה נחסמה על ידי הדפדפן. יש לפנות לתמיכה.');
        return;
      }
      if (code === 'upload_failed') {
        fail('העלאת הקובץ נכשלה. נסה שוב.');
        return;
      }
      if (code === 'timeout') {
        fail('החילוץ נמשך זמן רב מדי. נסה שוב מאוחר יותר.');
        return;
      }
      if (code === 'empty') {
        fail('לא נמצא טקסט במסמך');
        return;
      }
      if (code === 'ocr_failed') {
        fail('חילוץ הטקסט נכשל בשרת');
        return;
      }

      console.error('Extraction Failed:', error);
      fail((error as { message?: string })?.message || 'שגיאה בחילוץ המסמך');
    } finally {
      abortRef.current = null;
      setIsUploading(false);
      setIsProcessing(false);
    }
  };

  const handleCancel = () => {
    abortRef.current?.abort();
  };

  const handleCloseSidebar = () => {
    setSidebarOpen(false);
    setProgress(null);
    setErrorMessage('');
  };

  const handleRetry = () => {
    handleExtract();
  };

  return (
    <>
      <div className="w-full max-w-4xl mx-auto">
        <Card className="w-full mb-6 bg-background">
          <CardHeader className="space-y-2 pb-8">
            <div className="flex flex-col items-stretch justify-between gap-4 md:flex-row md:items-center">
              <div className="space-y-1">
                <CardTitle className="text-xl font-bold text-chen-900 md:text-2xl">
                  חילוץ טקסט מ-PDF
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  העלה קובץ PDF לחילוץ טקסט באמצעות מנוע OCR מתקדם
                </p>
              </div>
              <QuotaDisplay />
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Large Drop Zone */}
            <input
              ref={fileInputRef}
              id="pdf-upload"
              type="file"
              accept=".pdf"
              onChange={handleFileChange}
              className="hidden"
            />

            {!file ? (
              <Label
                htmlFor="pdf-upload"
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`flex min-h-[180px] cursor-pointer flex-col items-center justify-center gap-6 rounded-lg border-2 border-dashed p-6 transition-all duration-200 md:min-h-[320px] md:p-16 ${
                  isDragging
                    ? 'border-chen-900 bg-chen-900/10'
                    : 'border-border hover:border-chen-900/50 bg-background'
                }`}
              >
                <div className="flex items-center justify-center w-20 h-20 rounded-full bg-chen-900/10">
                  <FileText className="h-10 w-10 text-chen-900" />
                </div>
                <div className="text-center space-y-2">
                  <p className="text-lg font-semibold">
                    גרור קובץ PDF לכאן או לחץ לבחירה
                  </p>
                  <p className="text-sm text-muted-foreground">
                    תמיכה ב-PDF (מקסימום 300MB).
                  </p>
                </div>
                <Button
                  type="button"
                  variant="secondary"
                  className="mt-2 pointer-events-none"
                >
                  בחר קבצים
                </Button>
              </Label>
            ) : (
              <div className="flex min-h-[180px] flex-col items-center justify-center gap-6 rounded-lg border-2 border-dashed border-chen-900/30 bg-chen-900/5 p-6 md:min-h-[320px] md:p-16">
                <div className="flex items-center justify-center w-20 h-20 rounded-full bg-chen-900/10">
                  <FileText className="h-10 w-10 text-chen-900" />
                </div>
                <div className="text-center space-y-2">
                  <p className="text-lg font-semibold">{file.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={resetFile}
                  disabled={isUploading || isProcessing}
                  className="text-sm"
                >
                  החלף קובץ
                </Button>
              </div>
            )}

            {/* Bottom Controls Section */}
            <div className="flex flex-col items-stretch justify-between gap-4 rounded-lg border border-border bg-muted/30 p-4 sm:flex-row sm:items-center sm:p-6">
              <div className="flex items-center gap-3">
                <Switch
                  id="scan-mode"
                  checked={isScanned}
                  onCheckedChange={setIsScanned}
                  disabled={isUploading || isProcessing}
                />
                <div className="space-y-0.5">
                  <Label
                    htmlFor="scan-mode"
                    className="text-sm font-medium cursor-pointer"
                  >
                    מסמך סרוק
                  </Label>
                  {/* Metadata only: the backend detects and OCRs scanned pages
                      on its own, so this flag no longer steers extraction. */}
                  <p className="text-xs text-muted-foreground">
                    סימון לצורכי תיוג בלבד — הזיהוי מתבצע אוטומטית
                  </p>
                </div>
              </div>

              <Button
                onClick={handleExtract}
                disabled={!file || isUploading || isProcessing}
                className="bg-gradient-to-r from-sky-400 to-indigo-500 text-white px-8 hover:opacity-90 transition-opacity"
                size="lg"
              >
                <Loader2
                  aria-hidden="true"
                  className={`ml-2 h-5 w-5 ${isUploading || isProcessing ? 'animate-spin' : 'invisible'}`}
                />
                <span>
                  {isUploading
                    ? 'מעלה...'
                    : isProcessing
                      ? 'מחלץ...'
                      : 'שלח לחילוץ'}
                </span>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Extraction Sidebar */}
      <ExtractionSidebar
        isOpen={sidebarOpen}
        fileName={file?.name || null}
        currentStep={currentStep}
        activity={progress?.activity}
        percent={progress?.percent}
        pagesProcessed={progress?.pagesProcessed}
        pagesTotal={progress?.pagesTotal}
        errorMessage={errorMessage}
        onClose={handleCloseSidebar}
        onRetry={handleRetry}
        onCancel={handleCancel}
      />
      <AlertDialog open={showFileTooLargeDialog} onOpenChange={setShowFileTooLargeDialog}>
        <AlertDialogContent dir="rtl" className="border-destructive bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-destructive">הקובץ גדול מדי</AlertDialogTitle>
            <AlertDialogDescription className="text-destructive">
              פצל או כווץ את הקובץ לפני ההעלאה.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              סגירה
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <InsufficientCreditsDialog
        open={insufficientCreditsOpen}
        onOpenChange={setInsufficientCreditsOpen}
        description="אין מספיק קרדיטים לחילוץ המסמך. פנו אלינו להשלמת יתרה."
        wall={creditWall?.wall ?? "insufficient_credits"}
        quotaWindowEndsAt={creditWall?.quotaWindowEndsAt}
      />
    </>
  );
}

export default PdfExtractor;
